import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import rough from "roughjs";
import { adjustPeakIfNecessary, adjustValleyIfNecessary } from "../../helpers/WeekReview/PeakValleyHelper";
import { FeelingColors, ImpactColors } from "../../Colors";

const PleasantnessLineChart = ({ data, animate = true, lineColor = 'white' }) => {
    const svgRef = useRef();
    const [curvePoints, setCurvePoints] = useState([]);
    const [drawPulse, setDrawPulse] = useState(false);
    const [ripplePeaks, setRipplePeaks] = useState();
    const [rippleValleys, setRippleValleys] = useState();
    const viewboxWidth = 330;
    const viewboxHeight = 260;

    useEffect(() => {
        const parsedData = data.dataForChart.map(d => ({
            date: new Date(d.date), // so this works with test dates too that are strings
            weight: d.weight,
        }));
        
        const margin = { top: 20, right: 20, bottom: 20, left: 20 };
        const innerWidth = viewboxWidth - margin.left - margin.right;
        const innerHeight = viewboxHeight - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current)
            .attr("viewBox", `0 0 ${viewboxWidth} ${viewboxHeight}`)
            .attr("preserveAspectRatio", "xMidYMid meet");
        // .style("border", "1px solid black");
        svg.selectAll("*").remove(); // Clear previous elements

        const rc = rough.svg(svg.node()); // Attach Rough.js to the SVG

        // #region Draw axes and labels

        const minDate = d3.timeDay.floor(parsedData[0].date);
        const maxDate = d3.timeDay.ceil(parsedData[parsedData.length - 1].date);
        const xScale = d3
            .scaleTime()
            .domain([minDate, maxDate])
            .range([0, innerWidth]);

        const yScale = d3.scaleLinear()
            .domain([-3.5, 3.5])
            .range([innerHeight, 0]);

        const axesGroup = svg.append("g");
        // Draw rough sketchy X-axis
        const xAxis = axesGroup.append("line")
            .attr("x1", xScale(minDate))
            .attr("y1", yScale(0))
            .attr("x2", xScale(maxDate))
            .attr("y2", yScale(0))
            .attr("stroke", "white")
            .attr("stroke-width", 1);

        // Draw Y-axis
        const startX = 0
        const startY = innerHeight + 5;
        const endY = 5;
        const yAxis = axesGroup.append("line")
            .attr("x1", startX)
            .attr("y1", startY)
            .attr("x2", startX)
            .attr("y2", endY)
            .attr("stroke", "white")
            .attr("stroke-width", 1);

        // Draw arrows
        const arrowheadSize = 10;

        const endX = xScale(maxDate);
        const arrowPathX = `
            M ${endX - arrowheadSize} ${innerHeight/2 - arrowheadSize} 
            L ${endX} ${innerHeight/2} 
            M ${endX - arrowheadSize} ${innerHeight/2 + arrowheadSize}
            L ${endX} ${innerHeight/2} 
        `;
        const arrowXAxis = rc.path(arrowPathX, { stroke: 'white', strokeWidth: 2, fill: 'white' });
        axesGroup.node().appendChild(arrowXAxis);

        const arrow1Path = `
            M ${startX - arrowheadSize} ${endY + arrowheadSize} 
            L ${startX} ${endY} 
            M ${startX + arrowheadSize} ${endY + arrowheadSize} 
            L ${startX} ${endY}
        `;
        const arrow2Path = `
            M ${startX - arrowheadSize} ${startY - arrowheadSize} 
            L ${startX} ${startY} 
            M ${startX + arrowheadSize} ${startY - arrowheadSize} 
            L ${startX} ${startY}
        `;

        const arrow1YAxis = rc.path(arrow1Path, { roughness: 1, stroke: 'white', strokeWidth: 2, fill: 'white' });
        const arrow2YAxis = rc.path(arrow2Path, { roughness: 1, stroke: 'white', strokeWidth: 2, fill: 'white' });
        axesGroup.node().appendChild(arrow1YAxis);
        axesGroup.node().appendChild(arrow2YAxis)
        // Add label for Y-axis
        const labelPositionX = startX - 3;
        const labelFontSize = 16;
        axesGroup.append("text")
            .attr("x", labelPositionX)
            .attr("y", 105)
            .attr("transform", `rotate(-90, ${labelPositionX}, ` + innerHeight / 2 + ")")
            .attr("text-anchor", "middle")
            .style("font-family", "Comic Sans MS, Comic Neue, Marker Felt, Chalkboard, sans-serif")
            .style("fill", "white")
            .style("font-size", labelFontSize)
            .text("pleasantness");

        //#endregion

        // #region draw Catmull rom from the bezier points

        const bezierCurveEndpoints = [];
        // A custom context to capture the points on the curve basis
        const addEndpointsToArray = {
            moveTo(x, y) {
                bezierCurveEndpoints.push({ x, y });
            },
            lineTo(x, y) {
                // Not needed here but part of the context interface
            },
            closePath() {
                // Not needed here but part of the context interface
            },
            bezierCurveTo(x1, y1, x2, y2, x, y) {
                // You can also capture control points here if needed
                bezierCurveEndpoints.push({ x, y });
            }
        };
        const getPointsOnBezierCurve = d3.line()
            .x(d => xScale(d.date))
            .y(d => yScale(d.weight))
            .curve(d3.curveBasisOpen)
            .context(addEndpointsToArray);

        getPointsOnBezierCurve(parsedData);
        adjustPeakIfNecessary(bezierCurveEndpoints, data.peakIndices, data.dataForChart);
        adjustValleyIfNecessary(bezierCurveEndpoints, data.valleyIndices, data.dataForChart);
        setCurvePoints(bezierCurveEndpoints);

        const generateCatmullRom = d3.line()
            .x(d => d.x)
            .y(d => d.y)
            .curve(d3.curveCatmullRom);

        // Draw catmull rom spline through the bezier endpoints
        const catmullRomCurveData = generateCatmullRom(bezierCurveEndpoints);
        const curvePath = svg.append("path")
            .attr("d", catmullRomCurveData)
            .attr("fill", "none")
            .attr("stroke", lineColor)
            .attr("stroke-width", 2);

        // Draw data points on the curve
        const circles = svg.selectAll("circle")
            .data(bezierCurveEndpoints)
            .enter()
            .append("circle")
            .attr("cx", point => point.x)
            .attr("cy", point => point.y)
            .attr("r", 1)
            .attr("fill", lineColor);

        // Draw the circles for peaks and valleys that will animate later
        const ripplePeaks = svg.append("g");
        data.peakIndices.forEach((index) => {
            ripplePeaks.append("circle")
                .attr("cx", bezierCurveEndpoints[index].x)
                .attr("cy", bezierCurveEndpoints[index].y)
                .attr("r", 3)
                .attr("fill", "none")
                .attr("stroke", "green")
                .attr("stroke-width", 2)
                .style("opacity", 0);
        })

        const rippleValleys = svg.append("g");
        data.valleyIndices.forEach((index) => {
            rippleValleys.append("circle")
                .attr("cx", bezierCurveEndpoints[index].x)
                .attr("cy", bezierCurveEndpoints[index].y)
                .attr("r", 3)
                .attr("fill", "none")
                .attr("stroke", FeelingColors["Not good"])
                .attr("stroke-width", 2)
                .style("opacity", 0);
        })

        //#endregion

        // Shift the drawn elements a little bit to make them centered in svg
        const xAxisLength = xScale(maxDate) - xScale(minDate); // this is the longest element in the chart
        const remainingSpaceInSVG = viewboxWidth - xAxisLength - labelPositionX - labelFontSize; // this is = 27
        const xOffsetForPaths = remainingSpaceInSVG;
        axesGroup.attr("transform", `translate(${xOffsetForPaths}, 0)`);
        curvePath.attr("transform", `translate(${xOffsetForPaths - 5}, 0)`);
        circles.attr("transform", `translate(${xOffsetForPaths - 5}, 0)`);
        ripplePeaks.attr("transform", `translate(${xOffsetForPaths - 5}, 0)`);
        setRipplePeaks(ripplePeaks);
        rippleValleys.attr("transform", `translate(${xOffsetForPaths - 5}, 0)`);
        setRippleValleys(rippleValleys);

        if (animate) {
            const totalLength = curvePath.node().getTotalLength();
            curvePath.attr("stroke-dasharray", totalLength + " " + totalLength)
                .attr("stroke-dashoffset", totalLength)
                .transition()
                .duration(2800)
                .ease(d3.easeLinear)
                .attr("stroke-dashoffset", 0)
                .on("end", () => {
                    // animate peak and valley circles now
                    setDrawPulse(true);
                });
        } 
        else {
            setDrawPulse(true);
        }

    }, [data])

    const rippleEffect = (rippleGroup, indices, endpoints, color) => {
        indices.forEach((index) => {
            rippleGroup.selectAll("circle")
                .style("opacity", 1);

            const createRipple = () => {
                rippleGroup
                    .append("circle")
                    .attr("cx", endpoints[index].x)
                    .attr("cy", endpoints[index].y)
                    .attr("r", 2) // Start small
                    .attr("fill", "none")
                    .attr("stroke", color)
                    .attr("stroke-width", 3)
                    .attr("opacity", 1)
                    .transition()
                    .duration(1500)
                    .ease(d3.easeCubicOut)
                    .attr("r", 10) // Expand outward
                    .attr("opacity", 0.1) // Fade out
                    .on("end", function () {
                        d3.select(this).remove(); // Clean up after animation
                    });
            };

            createRipple();
            d3.interval(createRipple, 2000); // Repeat every 3s
        });
    }

    useEffect(() => {
        if (drawPulse) {
            rippleEffect(ripplePeaks, data.peakIndices, curvePoints, FeelingColors.Good);
            rippleEffect(rippleValleys, data.valleyIndices, curvePoints, ImpactColors.Depleting);
        }
    }, [drawPulse, data])

    return (
        <div style={{ width: "100%", height: "auto", display: "flex", justifyContent: "center" }}>
            <svg ref={svgRef} width="100%" />
        </div>
    );
}

export default PleasantnessLineChart;